import type { CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import type { TestCredentialsResult } from '@/api/hooks/useTenantConfig'
import { useTestInstanceCredentialsDirect } from '@/api/hooks/useTenantConfig'
import { SeverityTag } from '@/components/Display/SeverityDisplay'
import { TestCredentialsModal } from '@/pages/TenantConfiguration/components/TestCredentialsModal'
import { useTenantStore } from '@/stores/tenantStore'
import {
  ApiOutlined,
  CheckCircleOutlined,
  DisconnectOutlined,
  LinkOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  GraviButton,
  Horizontal,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { Card, Descriptions } from 'antd'
import _ from 'lodash'
import { useState } from 'react'
import {
  buildFixMappingUrl,
  canFixMapping,
  getResponsibleTenant,
} from './utils'

const CARD_STYLE = { marginBottom: 16 }

const SUCCESS_ICON_STYLE = { color: 'var(--theme-success)' }
const ERROR_ICON_STYLE = { color: 'var(--theme-error)' }
const WARNING_ICON_STYLE = { color: 'var(--theme-warning)' }

const MAPPING_FAILURE_CARD_STYLE = { marginBottom: 16, borderRadius: '8px' }
const WARNING_CARD_STYLE = {
  backgroundColor: 'var(--theme-warning-dim)',
  border: '1px solid var(--theme-warning)',
}
const OWNERSHIP_CARD_STYLE = {
  backgroundColor: 'var(--bg-2)',
  marginBottom: '16px',
}
const WARNING_ICON_18_STYLE = {
  fontSize: '18px',
  color: 'var(--theme-warning)',
}
const TEXT_ICON_18_STYLE = { fontSize: '18px', color: 'var(--text-color)' }

const SUPPLY_CONTEXT_CARD_STYLE = {
  backgroundColor: 'var(--bg-2)',
  marginTop: 8,
}

const DESCRIPTIONS_CONTENT_STYLE = {
  display: 'flex',
  justifyContent: 'flex-end',
}

const LOAD_DESCRIPTIONS_STYLE = { paddingLeft: 16 }
const WARNINGS_LIST_STYLE = { paddingLeft: 4 }
const PRE_WRAP_STYLE = { whiteSpace: 'pre-wrap' as const }

export type SupplyContextDropData = {
  product: string
  loads: {
    terminals: string[]
    suppliers: string[]
    products: string[]
    price_types: string[]
    contracts: string[]
    load_number?: string | null
  }[]
}

type SupplyContextCardProps = {
  drops: SupplyContextDropData[]
}

export function SupplyContextCard({ drops }: SupplyContextCardProps) {
  return (
    <Card size='small' style={SUPPLY_CONTEXT_CARD_STYLE}>
      <Vertical gap={12}>
        <Texto weight='600'>Supply Context</Texto>
        {drops.map((drop, i) => (
          <Vertical key={i} gap={4}>
            <Texto weight='700'>
              Drop {i + 1} — {drop.product}
            </Texto>
            {drop.loads.map((load, j) => (
              <Descriptions
                key={j}
                column={1}
                size='small'
                style={LOAD_DESCRIPTIONS_STYLE}
                contentStyle={DESCRIPTIONS_CONTENT_STYLE}
              >
                {load.products.length > 0 && (
                  <Descriptions.Item label='Product'>
                    {load.products.join(' OR ')}
                  </Descriptions.Item>
                )}
                {load.terminals.length > 0 && (
                  <Descriptions.Item label='Terminal'>
                    {load.terminals.join(' OR ')}
                  </Descriptions.Item>
                )}
                {load.suppliers.length > 0 && (
                  <Descriptions.Item label='Supplier'>
                    {load.suppliers.join(' OR ')}
                  </Descriptions.Item>
                )}
                {load.price_types.length > 0 && (
                  <Descriptions.Item label='Price Type'>
                    {load.price_types.map((pt) => _.startCase(pt)).join(' OR ')}
                  </Descriptions.Item>
                )}
                {load.contracts.length > 0 && (
                  <Descriptions.Item label='Contract'>
                    {load.contracts.join(' OR ')}
                  </Descriptions.Item>
                )}
                {load.load_number && (
                  <Descriptions.Item label='Load Number'>
                    {load.load_number}
                  </Descriptions.Item>
                )}
              </Descriptions>
            ))}
          </Vertical>
        ))}
      </Vertical>
    </Card>
  )
}

type LogDetailStatusCardProps = {
  log: CrossroadsLog
}

export function LogDetailStatusCard({ log }: LogDetailStatusCardProps) {
  const isWarning = log.status === 'succeeded' && log.severity === 'low'
  const isSuccess = log.status === 'succeeded' && !isWarning
  const isFailed = log.status === 'failed'

  if (!isSuccess && !isFailed && !isWarning) return null

  let titleText = log.title
  let iconStyle = ERROR_ICON_STYLE
  let Icon = WarningOutlined
  if (isWarning) {
    titleText = 'Completed with Warnings'
    iconStyle = WARNING_ICON_STYLE
  } else if (isSuccess) {
    titleText = 'Success'
    iconStyle = SUCCESS_ICON_STYLE
    Icon = CheckCircleOutlined
  }

  const warnings =
    isWarning && log.detail?.category === 'success' && 'warnings' in log.detail
      ? (log.detail.warnings as string[])
      : []

  return (
    <Card
      title={
        <Horizontal gap={8} alignItems='center'>
          <Icon style={iconStyle} />
          <span>{titleText}</span>
        </Horizontal>
      }
      size='small'
      style={CARD_STYLE}
    >
      <Vertical gap={8}>
        <Texto style={PRE_WRAP_STYLE}>{log.detail?.message}</Texto>
        {log.detail?.category === 'rejection' &&
          Array.isArray(log.detail.supply_context) && (
            <SupplyContextCard
              drops={log.detail.supply_context as SupplyContextDropData[]}
            />
          )}
        {warnings.length > 0 && (
          <Vertical gap={4} style={WARNINGS_LIST_STYLE}>
            {warnings.map((w, i) => (
              <Horizontal key={i} gap={8} alignItems='baseline'>
                <Texto appearance='hint'>—</Texto>
                <Texto>{w}</Texto>
              </Horizontal>
            ))}
          </Vertical>
        )}
        {isFailed && log.severity && (
          <Horizontal gap={8} alignItems='center'>
            <Texto appearance='hint'>Severity:</Texto>
            <SeverityTag severity={log.severity} />
          </Horizontal>
        )}
      </Vertical>
    </Card>
  )
}

type AuthenticationFailureCardProps = {
  log: CrossroadsLog
}

export function AuthenticationFailureCard({
  log,
}: AuthenticationFailureCardProps) {
  const [isTestModalOpen, setIsTestModalOpen] = useState(false)
  const [testResult, setTestResult] = useState<TestCredentialsResult | null>(
    null,
  )
  const testMutation = useTestInstanceCredentialsDirect()

  const detail = log.detail as unknown as {
    category: 'authentication'
    message?: string
    tenant_id?: string | null
    instance_id?: string | null
  }
  const canTest = !!detail.tenant_id && !!detail.instance_id

  const handleTestCredentials = () => {
    if (!detail.tenant_id || !detail.instance_id) return
    setTestResult(null)
    setIsTestModalOpen(true)
    testMutation.mutate(
      { tenantId: detail.tenant_id, instanceId: detail.instance_id },
      {
        onSuccess: (data) => setTestResult(data),
        onError: () => setIsTestModalOpen(false),
      },
    )
  }

  return (
    <>
      <Card
        title={
          <Horizontal gap={8} alignItems='center'>
            <WarningOutlined style={ERROR_ICON_STYLE} />
            <span>{log.title}</span>
          </Horizontal>
        }
        size='small'
        style={CARD_STYLE}
      >
        <Vertical gap={8}>
          <Texto>{log.detail?.message}</Texto>
          {log.severity && (
            <Horizontal gap={8} alignItems='center'>
              <Texto appearance='hint'>Severity:</Texto>
              <SeverityTag severity={log.severity} />
            </Horizontal>
          )}
          <GraviButton
            theme2
            icon={<ApiOutlined />}
            onClick={handleTestCredentials}
            disabled={!canTest}
          >
            Test Credentials
          </GraviButton>
        </Vertical>
      </Card>
      <TestCredentialsModal
        open={isTestModalOpen}
        onClose={() => {
          setIsTestModalOpen(false)
          setTestResult(null)
        }}
        result={testResult}
        loading={testMutation.isPending}
      />
    </>
  )
}

type MappingFailureCardProps = {
  log: CrossroadsLog
}

export function MappingFailureCard({ log }: MappingFailureCardProps) {
  const { selectedTenant } = useTenantStore()
  const responsibleTenant = getResponsibleTenant(log)
  const isOwner = canFixMapping(log, selectedTenant?.name)

  const handleFixMapping = () => {
    const url = buildFixMappingUrl(log)
    if (url) window.open(url, '_blank')
  }

  return (
    <Card
      title={
        <Horizontal gap={12} verticalCenter>
          <DisconnectOutlined style={ERROR_ICON_STYLE} />
          <Texto category='h5' weight='600'>
            Mapping Issue Detected
          </Texto>
        </Horizontal>
      }
      style={MAPPING_FAILURE_CARD_STYLE}
    >
      <Vertical gap={16}>
        {log.severity && (
          <Horizontal gap={8} alignItems='center'>
            <Texto appearance='hint'>Severity:</Texto>
            <SeverityTag severity={log.severity} />
          </Horizontal>
        )}
        <Card size='small' style={WARNING_CARD_STYLE}>
          <Vertical gap={4}>
            <Horizontal alignItems='center' gap={8}>
              <WarningOutlined style={WARNING_ICON_18_STYLE} />
              <Texto weight='600'>What's the problem?</Texto>
            </Horizontal>
            <Texto>{log.detail.message}</Texto>
          </Vertical>
        </Card>
        {responsibleTenant && (
          <Card size='small' style={OWNERSHIP_CARD_STYLE}>
            <Horizontal gap={12} verticalCenter>
              <UserOutlined style={TEXT_ICON_18_STYLE} />
              <Vertical gap={4}>
                <Horizontal justifyContent='space-between' alignItems='center'>
                  <Texto weight='600'>
                    {isOwner
                      ? 'You are the owner of this issue, and will need to resolve it.'
                      : `${responsibleTenant} is the owner of this issue.`}
                  </Texto>
                  {isOwner && (
                    <GraviButton
                      theme2
                      icon={<LinkOutlined />}
                      onClick={handleFixMapping}
                    >
                      Fix Mapping
                    </GraviButton>
                  )}
                </Horizontal>
              </Vertical>
            </Horizontal>
          </Card>
        )}
      </Vertical>
    </Card>
  )
}
